import { Epic, Story, Sprint } from '../types';

/**
 * Export utilities for different formats
 */

// Export to CSV
export const exportToCSV = (data: any[], filename: string) => {
  if (data.length === 0) return;

  const headers = Object.keys(data[0]);
  const csvContent = [
    headers.join(','),
    ...data.map(row =>
      headers.map(header => {
        const value = row[header];
        // Escape commas and quotes
        const escaped = String(value).replace(/"/g, '""');
        return `"${escaped}"`;
      }).join(',')
    ),
  ].join('\n');

  downloadFile(csvContent, filename, 'text/csv');
};

// Export epics to CSV
export const exportEpicsToCSV = (epics: Epic[]) => {
  const data = epics.map(epic => ({
    ID: epic.id,
    Title: epic.title,
    Description: epic.description || '',
    Status: epic.status,
    Priority: epic.priority,
    StartDate: epic.startDate,
    EndDate: epic.endDate,
    Progress: epic.progress,
  }));

  exportToCSV(data, `epics-${new Date().toISOString().split('T')[0]}.csv`);
};

// Export stories to CSV
export const exportStoriesToCSV = (stories: Story[]) => {
  const data = stories.map(story => ({
    ID: story.id,
    Title: story.title,
    Description: story.description || '',
    Status: story.status,
    Priority: story.priority,
    Assignee: story.assignee,
    EpicID: story.epicId,
    SprintID: story.sprintId,
  }));

  exportToCSV(data, `stories-${new Date().toISOString().split('T')[0]}.csv`);
};

// Export sprints to CSV
export const exportSprintsToCSV = (sprints: Sprint[]) => {
  const data = sprints.map(sprint => ({
    ID: sprint.id,
    Name: sprint.name,
    StartDate: sprint.startDate,
    EndDate: sprint.endDate,
    Status: sprint.status,
    Goal: sprint.goal || '',
  }));

  exportToCSV(data, `sprints-${new Date().toISOString().split('T')[0]}.csv`);
};

// Export to JSON
export const exportToJSON = (data: any, filename: string) => {
  const jsonContent = JSON.stringify(data, null, 2);
  downloadFile(jsonContent, filename, 'application/json');
};

// Export all data as JSON
export const exportAllDataToJSON = (epics: Epic[], stories: Story[], sprints: Sprint[]) => {
  const data = {
    exportDate: new Date().toISOString(),
    epics,
    stories,
    sprints,
    summary: {
      totalEpics: epics.length,
      totalStories: stories.length,
      totalSprints: sprints.length,
    },
  };

  exportToJSON(data, `jiraz-export-${new Date().toISOString().split('T')[0]}.json`);
};

// Print current view
export const printCurrentView = () => {
  window.print();
};

// Export view as image (using html2canvas)
// Note: Requires html2canvas package to be installed
// export const exportViewAsImage = async (elementId: string, filename: string) => {
//   try {
//     const element = document.getElementById(elementId);
//     if (!element) {
//       console.error('Element not found');
//       return;
//     }
//     const html2canvas = (await import('html2canvas')).default;
//     const canvas = await html2canvas(element, {
//       backgroundColor: '#f5f5f7',
//       scale: 2,
//     });
//     canvas.toBlob((blob) => {
//       if (blob) {
//         const url = URL.createObjectURL(blob);
//         const link = document.createElement('a');
//         link.href = url;
//         link.download = filename;
//         link.click();
//         URL.revokeObjectURL(url);
//       }
//     });
//   } catch (error) {
//     console.error('Failed to export image:', error);
//   }
// };

// Generate PDF report
// Note: Requires jspdf package to be installed
// export const generatePDFReport = async (
//   epics: Epic[],
//   stories: Story[],
//   sprints: Sprint[]
// ) => {
//   try {
//     const jsPDF = (await import('jspdf')).default;
//     const doc = new jsPDF();
//     let yPos = 20;
//     doc.setFontSize(20);
//     doc.text('Jiraz Timeline Report', 20, yPos);
//     yPos += 15;
//     doc.setFontSize(10);
//     doc.text(`Generated: ${new Date().toLocaleDateString()}`, 20, yPos);
//     yPos += 15;
//     doc.setFontSize(14);
//     doc.text('Summary', 20, yPos);
//     yPos += 10;
//     doc.setFontSize(10);
//     doc.text(`Total Epics: ${epics.length}`, 25, yPos);
//     yPos += 7;
//     doc.text(`Total Stories: ${stories.length}`, 25, yPos);
//     yPos += 7;
//     doc.text(`Total Sprints: ${sprints.length}`, 25, yPos);
//     yPos += 15;
//     if (epics.length > 0) {
//       doc.setFontSize(14);
//       doc.text('Epics', 20, yPos);
//       yPos += 10;
//       doc.setFontSize(9);
//       epics.slice(0, 10).forEach((epic) => {
//         if (yPos > 270) {
//           doc.addPage();
//           yPos = 20;
//         }
//         doc.text(`• ${epic.id}: ${epic.title} (${epic.status})`, 25, yPos);
//         yPos += 7;
//       });
//     }
//     doc.save(`jiraz-report-${new Date().toISOString().split('T')[0]}.pdf`);
//   } catch (error) {
//     console.error('Failed to generate PDF:', error);
//   }
// };

// Helper: Download file
const downloadFile = (content: string, filename: string, mimeType: string) => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Copy data to clipboard
export const copyToClipboard = async (text: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (error) {
    console.error('Failed to copy to clipboard:', error);
    return false;
  }
};

// Share via Web Share API
export const shareContent = async (title: string, text: string, url?: string) => {
  if (navigator.share) {
    try {
      await navigator.share({
        title,
        text,
        url: url || window.location.href,
      });
      return true;
    } catch (error) {
      console.error('Failed to share:', error);
      return false;
    }
  } else {
    // Fallback: copy to clipboard
    return copyToClipboard(`${title}\n\n${text}\n\n${url || window.location.href}`);
  }
};
